import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, Users, Calendar, 
  Download, Filter, BarChart3, PieChart,
  Activity, Target, Award, Clock, MapPin, Search
} from 'lucide-react';
import { agencyService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { COUNTRIES } from '../../utils/countryUtils';
import './AdminAnalytics.css';

const AdminAnalytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  // Set default tab based on user role
  const [selectedTab, setSelectedTab] = useState('overview');
  const [userRoleLoaded, setUserRoleLoaded] = useState(false);
  
  // Update selected tab when user role is loaded
  useEffect(() => {
    if (user?.role) {
      setUserRoleLoaded(true);
      if (user.role === 'agents_manager') {
        setSelectedTab('locations');
      } else {
        setSelectedTab('overview');
      }
    } else {
      setUserRoleLoaded(false);
    }
  }, [user?.role]);
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalRevenue: 0,
      totalBookings: 0,
      averageBookingValue: 0,
      conversionRate: 0,
      topSuppliers: [],
      revenueByMonth: [],
      totalQuotes: 0,
      totalClients: 0
    },
    agents: {
      topPerformers: [],
      performanceMetrics: {
        totalAgents: 0,
        activeAgents: 0,
        performingAgents: 0,
        performanceRate: 0
      },
      revenueByAgent: []
    },
    bookings: {
      bySupplier: [],
      byCategory: [],
      byMonth: [],
      averageLeadTime: 0
    },
    trends: {
      monthlyRevenue: [],
      monthlyBookings: [],
      supplierTrends: []
    }
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      // Map dateRange to period parameter
      const periodMap = {
        7: 'week',
        30: 'month',
        90: 'quarter',
        365: 'year'
      };

      // Use agency analytics endpoint with correct parameters
      const data = await agencyService.getAnalytics({
        period: periodMap[parseInt(dateRange)] || 'month'
      });

      // Calculate overview metrics from the API response
      const totalRevenue = data.revenue_by_agent?.reduce((sum, agent) => sum + parseFloat(agent.total_revenue || 0), 0) || 0;
      const totalQuotes = data.conversion_rates?.total_quotes || 0;
      const totalBookings = data.client_metrics?.clients_with_bookings || 0; // This will be actual completed itineraries
      const averageBookingValue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
      const conversionRate = data.conversion_rates?.conversion_rate || 0;

      // Process the data from the API
      setAnalyticsData({
        overview: {
          totalRevenue: totalRevenue,
          totalBookings: totalBookings,
          totalQuotes: totalQuotes,
          averageBookingValue: averageBookingValue,
          conversionRate: conversionRate,
          topSuppliers: data.popular_suppliers || [],
          revenueByMonth: data.monthly_trend || [],
          totalClients: data.client_metrics?.total_clients || 0
        },
        agents: {
          topPerformers: data.topAgents || [],
          performanceMetrics: data.agentMetrics || {
            totalAgents: 0,
            activeAgents: 0,
            performingAgents: 0,
            performanceRate: 0
          },
          revenueByAgent: data.revenueByAgent || []
        },
        bookings: {
          bySupplier: data.topSuppliers || [],
          byCategory: data.bookingsByCategory || [],
          byMonth: data.revenueByMonth || [],
          averageLeadTime: data.averageLeadTime || 0
        },
        trends: {
          monthlyRevenue: data.monthly_trend || [],
          monthlyBookings: data.monthly_trend || [],
          supplierTrends: data.popular_suppliers || []
        }
      });



    } catch (error) {
      console.error('Failed to load analytics data:', error);
      
      // Set default empty data to prevent UI errors
      setAnalyticsData({
        overview: {
          totalRevenue: 0,
          totalBookings: 0,
          totalQuotes: 0,
          averageBookingValue: 0,
          conversionRate: 0,
          topSuppliers: [],
          revenueByMonth: [],
          totalClients: 0
        },
        agents: {
          topPerformers: [],
          performanceMetrics: {
            totalAgents: 0,
            activeAgents: 0,
            performingAgents: 0,
            performanceRate: 0
          },
          revenueByAgent: []
        },
        bookings: {
          bySupplier: [],
          byCategory: [],
          byMonth: [],
          averageLeadTime: 0
        },
        trends: {
          monthlyRevenue: [],
          monthlyBookings: [],
          supplierTrends: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const determineCategory = (supplier) => {
    // Simple categorization logic - enhance as needed
    if (!supplier) return 'Other';
    const lowerSupplier = supplier.toLowerCase();
    if (lowerSupplier.includes('cruise')) return 'Cruises';
    if (lowerSupplier.includes('hotel') || lowerSupplier.includes('marriott') || lowerSupplier.includes('hilton')) return 'Hotels';
    if (lowerSupplier.includes('disney') || lowerSupplier.includes('universal')) return 'Theme Parks';
    if (lowerSupplier.includes('delta') || lowerSupplier.includes('united') || lowerSupplier.includes('american')) return 'Flights';
    return 'Other';
  };

  const calculateAgentMetrics = (agents, agentPerformance) => {
    const activeAgents = agents.filter(a => a.status === 'active').length;
    const performingAgents = Object.keys(agentPerformance).length;
    
    return {
      totalAgents: agents.length,
      activeAgents,
      performingAgents,
      performanceRate: activeAgents > 0 ? (performingAgents / activeAgents * 100).toFixed(1) : 0
    };
  };

  const exportData = () => {
    // Convert analytics data to CSV
    const csvData = convertToCSV(analyticsData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    // Simple CSV conversion - enhance as needed
    let csv = 'Analytics Report\n';
    csv += `Date Range: Last ${dateRange} days\n\n`;
    
    csv += 'Overview Metrics\n';
    csv += `Total Revenue,$${data.overview.totalRevenue.toFixed(2)}\n`;
    csv += `Total Bookings,${data.overview.totalBookings}\n`;
    csv += `Average Booking Value,$${data.overview.averageBookingValue.toFixed(2)}\n\n`;
    
    csv += 'Top Suppliers\n';
    csv += 'Supplier,Revenue,Bookings\n';
    data.overview.topSuppliers.forEach(s => {
      csv += `${s.supplier},$${s.revenue.toFixed(2)},${s.bookings}\n`;
    });
    
    return csv;
  };

  const allTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3, roles: ['super_admin', 'agency_admin'] },
    { id: 'agents', label: 'Agent Performance', icon: Users, roles: ['super_admin', 'agency_admin'] },
    { id: 'locations', label: 'Agent Locations', icon: MapPin, roles: ['super_admin', 'agency_admin', 'agents_manager'] },
    { id: 'bookings', label: 'Bookings Analysis', icon: Activity, roles: ['super_admin', 'agency_admin'] },
    { id: 'clients', label: 'Client Insights', icon: Target, roles: ['super_admin', 'agency_admin'] }
  ];

  // Filter tabs based on user role - simple and direct
  const tabs = user?.role === 'agents_manager' 
    ? allTabs.filter(tab => tab.id === 'locations')
    : allTabs.filter(tab => tab.roles.includes(user?.role || ''));

  // Show loading until we have user role
  if (loading || authLoading || !user?.role) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
            <p className="text-gray-600 mt-2">Track your agency's performance and insights</p>
          </div>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            {/* Hide date range filter for locations tab since agent locations are static data */}
            {selectedTab !== 'locations' && (
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last 12 months</option>
              </select>
            )}
            <button
              onClick={exportData}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-8">
        <nav className="flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center pb-4 px-1 border-b-2 transition-colors ${
                selectedTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="analytics-content">
        {selectedTab === 'overview' && <OverviewTab data={analyticsData.overview} />}
        {selectedTab === 'agents' && <AgentsTab data={analyticsData.agents} />}
        {selectedTab === 'locations' && <AgentLocationsTab />}
        {selectedTab === 'bookings' && <BookingsTab data={analyticsData.bookings} />}
        {selectedTab === 'clients' && <ClientsTab data={analyticsData.clients} />}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ data }) => {
  const metrics = [
    { 
      label: 'Total Revenue', 
      value: `$${data.totalRevenue.toLocaleString()}`, 
      icon: DollarSign,
      color: 'green'
    },
    { 
      label: 'Total Bookings', 
      value: data.totalBookings, 
      icon: Activity,
      color: 'blue'
    },
    { 
      label: 'Average Booking Value', 
      value: `$${data.averageBookingValue.toFixed(0)}`, 
      icon: TrendingUp,
      color: 'purple'
    },
    { 
      label: 'Conversion Rate', 
      value: `${data.conversionRate}%`, 
      icon: Target,
      color: 'orange'
    }
  ];

  return (
    <div>
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{metric.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue by Month */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
          <div className="h-64 flex items-center justify-center text-gray-500">
            {/* Placeholder for chart - integrate with a charting library */}
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Revenue chart will be displayed here</p>
            </div>
          </div>
        </div>

        {/* Top Suppliers */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Suppliers</h3>
          <div className="space-y-4">
            {Array.isArray(data.topSuppliers) && data.topSuppliers.map((supplier, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{supplier.supplier || 'N/A'}</p>
                    <p className="text-sm text-gray-500">{supplier.booking_count || supplier.bookings || 0} bookings</p>
                  </div>
                </div>
                <p className="font-semibold text-gray-900">
                  ${(supplier.total_revenue || supplier.revenue || 0).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Agents Tab Component
const AgentsTab = ({ data }) => {
  return (
    <div>
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Total Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.totalAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Active Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.activeAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Performing Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.performingAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Performance Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.performanceRate}%</p>
        </div>
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Agents</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Commission
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.topPerformers.map((agent, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-sm font-medium text-blue-600">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{agent.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.bookings}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${agent.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${agent.averageCommission.toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Bookings Tab Component
const BookingsTab = ({ data }) => {
  return (
    <div>
      {/* Bookings by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bookings by Category</h3>
          <div className="space-y-4">
            {data.byCategory.map((category, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{category.category}</span>
                  <span className="text-sm text-gray-500">{category.count} bookings</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${(category.count / Math.max(...data.byCategory.map(c => c.count))) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Average Lead Time */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Insights</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Lead Time</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{data.averageLeadTime} days</p>
              <p className="text-sm text-gray-500 mt-1">From quote to booking</p>
            </div>
            <div className="pt-6 border-t">
              <p className="text-sm font-medium text-gray-600">Peak Booking Month</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">March</p>
              <p className="text-sm text-gray-500 mt-1">45 bookings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings by Supplier Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Bookings by Supplier</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(data.bySupplier) && data.bySupplier.map((supplier, index) => {
                const revenue = supplier.total_revenue || supplier.revenue || 0;
                const bookings = supplier.booking_count || supplier.bookings || 0;
                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {supplier.supplier || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bookings}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${bookings > 0 ? (revenue / bookings).toFixed(0) : '0'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Clients Tab Component
const ClientsTab = ({ data }) => {
  return (
    <div>
      {/* Client Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Total Clients</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.totalClients}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">New This Month</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.newClientsThisMonth}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Repeat Clients</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.repeatClients}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Retention Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {data.totalClients > 0 ? ((data.repeatClients / data.totalClients) * 100).toFixed(1) : 0}%
          </p>
        </div>
      </div>

      {/* Placeholder for future client insights */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Client Insights Coming Soon</h3>
          <p className="text-gray-500">
            Detailed client demographics, preferences, and travel patterns will be available here.
          </p>
        </div>
      </div>
    </div>
  );
};

// Agent Locations Tab Component
const AgentLocationsTab = () => {
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [states, setStates] = useState([]);
  const [statesError, setStatesError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({
    state: [], // Changed to array for multi-select
    city: '',
    zipCode: '',
    country: '',
    // New impactful filters
    status: '',
    dataSource: '',
    dateRegisteredFrom: '',
    dateRegisteredTo: '',
    hasActivity: ''
  });
  const [searchResults, setSearchResults] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 50
  });
  const [statistics, setStatistics] = useState({
    totalAgents: 0,
    statesRepresented: 0,
    citiesRepresented: 0,
    withW9Data: 0
  });

  useEffect(() => {
    loadStates();
    loadCountries();
    loadAllAgents();
  }, []);

  const loadStates = async () => {
    try {
      setStatesError(false);
      const response = await agencyService.getAgentStates();
      setStates(Array.isArray(response?.states) ? response.states : []);
    } catch (error) {
      console.error('Failed to load states:', error);
      setStates([]); // Ensure states is always an array
      setStatesError(true);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await agencyService.getAgentCountries();
      setCountries(Array.isArray(response?.countries) ? response.countries : []);
    } catch (error) {
      console.error('Failed to load countries:', error);
      setCountries([]); // Ensure countries is always an array
    }
  };

  // Helper function to get country name from code
  const getCountryName = (countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  };

  const loadAllAgents = async (page = 1) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pagination.pageSize;
      const response = await agencyService.searchAgentsByLocation({
        ...filters,
        limit: pagination.pageSize,
        offset: offset,
        getStats: page === 1 // Only get stats on first page load
      });
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        console.error('Invalid response structure:', response);
        throw new Error('Invalid API response');
      }
      
      // Debug logging
      console.log('API Response:', {
        success: response.success,
        agentsCount: response.agents ? response.agents.length : 'undefined',
        totalCount: response.totalCount,
        statistics: response.statistics,
        filters: response.filters
      });
      
      setSearchResults(Array.isArray(response.agents) ? response.agents : []);
      setPagination({
        currentPage: Number(response.currentPage) || page,
        totalPages: Number(response.totalPages) || 1,
        totalCount: Number(response.totalCount) || 0,
        pageSize: Number(response.pageSize) || 50
      });
      
      // Update statistics if provided
      if (response.statistics && typeof response.statistics === 'object') {
        setStatistics({
          totalAgents: Number(response.statistics.totalAgents) || 0,
          statesRepresented: Number(response.statistics.statesRepresented) || 0,
          citiesRepresented: Number(response.statistics.citiesRepresented) || 0,
          withW9Data: Number(response.statistics.withW9Data) || 0,
          stateDistribution: response.statistics.stateDistribution || {}
        });
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
      setSearchResults([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 50
      });
      setStatistics({
        totalAgents: 0,
        statesRepresented: 0,
        citiesRepresented: 0,
        withW9Data: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (page = 1) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pagination.pageSize;
      const response = await agencyService.searchAgentsByLocation({
        ...filters,
        limit: pagination.pageSize,
        offset: offset,
        getStats: true // Always get stats for Top States display
      });
      
      // Validate response structure
      if (!response || typeof response !== 'object') {
        console.error('Invalid search response structure:', response);
        throw new Error('Invalid API response');
      }
      
      // Debug logging for search
      console.log('Search API Response:', {
        success: response.success,
        agentsCount: response.agents ? response.agents.length : 'undefined',
        totalCount: response.totalCount,
        statistics: response.statistics,
        filters: response.filters
      });
      
      setSearchResults(Array.isArray(response.agents) ? response.agents : []);
      setPagination({
        currentPage: Number(response.currentPage) || page,
        totalPages: Number(response.totalPages) || 1,
        totalCount: Number(response.totalCount) || 0,
        pageSize: Number(response.pageSize) || 50
      });
      
      // Update statistics if provided
      if (response.statistics && typeof response.statistics === 'object') {
        setStatistics({
          totalAgents: Number(response.statistics.totalAgents) || 0,
          statesRepresented: Number(response.statistics.statesRepresented) || 0,
          citiesRepresented: Number(response.statistics.citiesRepresented) || 0,
          withW9Data: Number(response.statistics.withW9Data) || 0,
          stateDistribution: response.statistics.stateDistribution || {}
        });
      }
    } catch (error) {
      console.error('Failed to search agents:', error);
      setSearchResults([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 50
      });
      setStatistics({
        totalAgents: 0,
        statesRepresented: 0,
        citiesRepresented: 0,
        withW9Data: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ 
      state: [], // Clear state array
      city: '', 
      zipCode: '', 
      country: '',
      status: '',
      dataSource: '',
      dateRegisteredFrom: '',
      dateRegisteredTo: '',
      hasActivity: ''
    });
    loadAllAgents(1); // Reload first page with no filters
  };

  const handlePageChange = (page) => {
    if (Object.values(filters).some(f => f)) {
      handleSearch(page);
    } else {
      loadAllAgents(page);
    }
  };

  const exportAgents = async () => {
    try {
      setLoading(true);
      
      // Fetch ALL filtered results for export (no pagination limits)
      const exportResponse = await agencyService.searchAgentsByLocation({
        ...filters,
        limit: 10000, // Large limit to get all results
        offset: 0,
        getStats: false // Don't need stats for export
      });
      
      const allFilteredAgents = Array.isArray(exportResponse.agents) ? exportResponse.agents : [];
      
      // Generate CSV with all filtered results
      const csvData = convertAgentsToCSV(allFilteredAgents);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Include filter info in filename if filters are applied
      let filterSuffix = '';
      if (filters.state && filters.state.length > 0) {
        // Include state abbreviations in filename (e.g., -UT-ID-WY-NV)
        filterSuffix = `-${filters.state.join('-')}`;
      } else if (Object.values(filters).some(f => f && (Array.isArray(f) ? f.length > 0 : true))) {
        filterSuffix = '-filtered';
      }
      a.download = `agent-locations${filterSuffix}-${new Date().toISOString().split('T')[0]}.csv`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      console.log(`Exported ${allFilteredAgents.length} agents to CSV`);
    } catch (error) {
      console.error('Failed to export agents:', error);
      alert('Failed to export agent data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const convertAgentsToCSV = (agentData) => {
    let csv = 'Agent Locations Report\n';
    csv += `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}\n`;
    csv += `Total Agents: ${agentData.length}\n`;
    
    // Add filter summary if filters are applied
    const activeFilters = Object.entries(filters).filter(([key, value]) => value);
    if (activeFilters.length > 0) {
      csv += `Filters Applied: ${activeFilters.map(([key, value]) => `${key}="${value}"`).join(', ')}\n`;
    }
    csv += '\n';
    
    // Enhanced CSV headers with more detail
    csv += 'Name,Email,Phone,Address,City,State,ZIP,Country,Agency,Status,Data Source,Created Date,W-9 Status,Profile Complete\n';
    
    agentData.forEach(agent => {
      const createdDate = agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : '';
      const w9Status = agent.location?.source === 'w9' ? 'Complete' : 'Missing';
      const profileComplete = (agent.location?.address && agent.email && agent.phone) ? 'Complete' : 'Incomplete';
      
      csv += `"${agent.name || ''}","${agent.email || ''}","${agent.phone || ''}","${agent.location?.address || ''}","${agent.location?.city || ''}","${agent.location?.state || ''}","${agent.location?.zipCode || ''}","${agent.location?.country || ''}","${agent.agency?.name || ''}","${agent.status || ''}","${agent.location?.source === 'w9' ? 'W-9 Form' : 'Registration'}","${createdDate}","${w9Status}","${profileComplete}"\n`;
    });
    
    // Add summary statistics at the end
    csv += '\n\nSummary Statistics:\n';
    const stateCount = new Set(agentData.map(a => a.location?.state).filter(Boolean)).size;
    const cityCount = new Set(agentData.map(a => a.location?.city).filter(Boolean)).size;
    const w9Count = agentData.filter(a => a.location?.source === 'w9').length;
    
    csv += `Unique States: ${stateCount}\n`;
    csv += `Unique Cities: ${cityCount}\n`;
    csv += `Agents with W-9 Data: ${w9Count}\n`;
    csv += `Agents with Registration Data Only: ${agentData.length - w9Count}\n`;
    
    return csv;
  };

  // Use statistics from API (reflects ALL agents, not just current page)
  const locationStats = statistics;

  // Use backend state distribution if available, otherwise calculate from current page
  console.log('Frontend state distribution debug:', {
    hasStatistics: !!statistics,
    hasStateDistribution: !!statistics?.stateDistribution,
    statisticsKeys: statistics ? Object.keys(statistics) : 'null',
    statisticsObject: statistics,
    stateDistributionSample: statistics?.stateDistribution ? Object.entries(statistics.stateDistribution).slice(0, 3) : 'undefined'
  });
  
  const stateDistribution = statistics?.stateDistribution || searchResults.reduce((acc, agent) => {
    const state = agent.location?.state;
    if (state) {
      acc[state] = (acc[state] || 0) + 1;
    }
    return acc;
  }, {});

  const topStates = Object.entries(stateDistribution)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10);

  return (
    <div>
      {/* Location Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{Number(locationStats.totalAgents) || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <MapPin className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">States</p>
              <p className="text-2xl font-bold text-gray-900">{Number(locationStats.statesRepresented) || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Target className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Cities</p>
              <p className="text-2xl font-bold text-gray-900">{Number(locationStats.citiesRepresented) || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Award className="w-8 h-8 text-orange-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">W-9 Complete</p>
              <p className="text-2xl font-bold text-gray-900">{Number(locationStats.withW9Data) || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Location Filters</h3>
          <div className="flex space-x-2">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={exportAgents}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-2" />
              {loading ? 'Exporting...' : 'Export All'}
            </button>
          </div>
        </div>
        
        {/* Location Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State(s) - {filters.state.length > 0 ? `${filters.state.length} selected` : 'None selected'}
            </label>
            <div className="border rounded-lg p-3 bg-white max-h-48 overflow-y-auto">
              {/* Select All / Clear All */}
              <div className="mb-2 pb-2 border-b">
                <button
                  onClick={() => {
                    if (filters.state.length === states.length) {
                      handleFilterChange('state', []);
                    } else {
                      handleFilterChange('state', [...states]);
                    }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {filters.state.length === states.length ? '✓ Clear All' : 'Select All'}
                </button>
              </div>
              {/* State Checkboxes */}
              {statesError ? (
                <p className="text-sm text-red-600">
                  Failed to load states.{' '}
                  <button
                    type="button"
                    onClick={loadStates}
                    className="underline hover:text-red-800 font-medium"
                  >
                    Retry
                  </button>
                </p>
              ) : Array.isArray(states) && states.length > 0 ? (
                states.map(state => (
                  <label key={state} className="flex items-center py-1 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.state.includes(state)}
                      onChange={(e) => {
                        const newStates = e.target.checked
                          ? [...filters.state, state]
                          : filters.state.filter(s => s !== state);
                        handleFilterChange('state', newStates);
                      }}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{state}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No states available</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input
              type="text"
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              placeholder="Enter city name"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
            <input
              type="text"
              value={filters.zipCode}
              onChange={(e) => handleFilterChange('zipCode', e.target.value)}
              placeholder="Enter ZIP code"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <select
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Countries</option>
              {countries.map(country => (
                <option key={country.code} value={country.code}>
                  {getCountryName(country.code)} ({country.count})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Business & Activity Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Source</label>
            <select
              value={filters.dataSource}
              onChange={(e) => handleFilterChange('dataSource', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sources</option>
              <option value="w9">W-9 Form</option>
              <option value="registration">Registration Only</option>
              <option value="both">Both Available</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Registered From</label>
            <input
              type="date"
              value={filters.dateRegisteredFrom}
              onChange={(e) => handleFilterChange('dateRegisteredFrom', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Registered To</label>
            <input
              type="date"
              value={filters.dateRegisteredTo}
              onChange={(e) => handleFilterChange('dateRegisteredTo', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
            <select
              value={filters.hasActivity}
              onChange={(e) => handleFilterChange('hasActivity', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Agents</option>
              <option value="recent">Recent Activity (90 days)</option>
              <option value="commissions">Has Commissions</option>
              <option value="quotes">Has Quotes</option>
              <option value="inactive">No Recent Activity</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* State Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top States</h3>
          <div className="space-y-3">
            {Array.isArray(topStates) && topStates.map(([state, count], index) => (
              <div key={state || index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                    {String(state || 'N/A').substring(0, 2)}
                  </div>
                  <span className="ml-3 font-medium text-gray-900">{String(state || 'N/A')}</span>
                </div>
                <span className="text-sm text-gray-500">{Number(count) || 0} agents</span>
              </div>
            ))}
          </div>
        </div>

        {/* Agent Results Table */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          <div className="p-6 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Agent Results ({Number(pagination.totalCount) || 0} total, showing {Array.isArray(searchResults) ? searchResults.length : 0})
            </h3>
            <div className="text-sm text-gray-500">
              Page {Number(pagination.currentPage) || 1} of {Number(pagination.totalPages) || 1}
            </div>
          </div>
          <div className="overflow-x-auto max-h-96">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(searchResults) && searchResults.length > 0 ? searchResults.map((agent, index) => {
                    // Ensure agent is an object and has required properties
                    if (!agent || typeof agent !== 'object') {
                      console.warn('Invalid agent data:', agent);
                      return null;
                    }
                    
                    return (
                      <tr key={agent.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {String(agent.name || 'N/A')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {String(agent.agency?.name || 'N/A')}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {agent.location?.city && agent.location?.state ? 
                              `${String(agent.location.city)}, ${String(agent.location.state)}` :
                              String(agent.location?.state || 'N/A')
                            }
                          </div>
                          <div className="text-sm text-gray-500">
                            {String(agent.location?.zipCode || '')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{String(agent.email || 'N/A')}</div>
                          <div className="text-sm text-gray-500">{String(agent.phone || 'N/A')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            agent.location?.source === 'w9' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {agent.location?.source === 'w9' ? 'W-9' : 'Registration'}
                          </span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                        {loading ? 'Loading...' : 'No agents found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          
          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {Math.max(1, ((Number(pagination.currentPage) - 1) * Number(pagination.pageSize)) + 1)} to{' '}
                {Math.min(Number(pagination.currentPage) * Number(pagination.pageSize), Number(pagination.totalCount))} of{' '}
                {Number(pagination.totalCount) || 0} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const startPage = Math.max(1, pagination.currentPage - 2);
                  const pageNum = startPage + i;
                  if (pageNum > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        pageNum === pagination.currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  className="px-3 py-1 text-sm bg-white border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
